import React, { forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Box, Chip, Grid, TextField, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';

import { BaseSection } from 'shared/sections/BaseSection/BaseSection';
import { SectionTitle } from 'shared/sections/SectionTitle/SectionTitle';
import { LocalizationContext } from 'contexts/LocalizationContext/LocalizationContext';
import { portfolioData } from 'data/portfolio.data';
import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import { PortfolioSectionItem } from 'shared/sections/PortfolioSection/PortfolioSectionItem';
import { SectionCTA } from 'shared/components/SectionCTA/SectionCTA';
import { FadeInOnScroll } from 'shared/components/FadeInOnScroll/FadeInOnScroll';
import { MenuItemElement } from 'contexts/LayoutContext/LayoutContext.types';
import { CarouselContainer } from 'shared/components/Carousel/CarouselContainer';

// Extract unique industries and countries from portfolio data
// Build options with counts
const industryCounts = portfolioData.reduce((acc, c) => {
  acc[c.industry] = (acc[c.industry] || 0) + 1;
  return acc;
}, {} as Record<string, number>);

const countryCounts = portfolioData.reduce((acc, c) => {
  acc[c.location.country] = (acc[c.location.country] || 0) + 1;
  return acc;
}, {} as Record<string, number>);

const industries = Object.keys(industryCounts).sort((a, b) => industryCounts[b] - industryCounts[a]);
const countries = Object.keys(countryCounts).sort((a, b) => countryCounts[b] - countryCounts[a]);
const countryLabels: Record<string, string> = {
  usa: 'USA', netherlands: 'Netherlands', switzerland: 'Switzerland', italy: 'Italy',
  france: 'France', ireland: 'Ireland', spain: 'Spain', poland: 'Poland',
  portugal: 'Portugal', uae: 'UAE', russia: 'Russia',
};

const useAnimatedCounters = (targets: number[], duration = 2000) => {
  const [values, setValues] = useState(targets.map(() => 0));
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const start = performance.now();
        const animate = (now: number) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setValues(targets.map(t => Math.round(eased * t)));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.3 });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  // eslint-disable-next-line
  }, [duration]);

  return { values, containerRef };
};

type PortfolioSectionProps = {
  autoUrlChange?: boolean;
}

export const PortfolioSection = forwardRef<MenuItemElement, PortfolioSectionProps>(({ autoUrlChange = true }, ref) => {
  const navigate = useNavigate();
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const { values: [revenue, users, businesses], containerRef: metricsRef } = useAnimatedCounters([76, 34, portfolioData.length]);

  const clients = useMemo(() => {
    return portfolioData.filter(({ industry, location }) => {
      if (selectedIndustries.length > 0 && !selectedIndustries.includes(industry)) return false;
      if (selectedCountries.length > 0 && !selectedCountries.includes(location.country)) return false;
      return true;
    });
  }, [selectedIndustries, selectedCountries]);

  const { dictionary } = useContext(LocalizationContext);
  const { techStackSectionRef } = useContext(LayoutContext);
  const { isMobile } = useContext(LayoutContext);

  // Auto-scroll carousel
  const carouselRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    let rafId: number;
    let paused = false;
    let pauseTimer: ReturnType<typeof setTimeout>;
    const speed = 0.5; // px per frame

    const step = () => {
      if (!paused && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= el.scrollWidth - el.clientWidth) {
          el.scrollLeft = 0;
        }
      }
      rafId = requestAnimationFrame(step);
    };

    const pauseScroll = () => {
      paused = true;
      clearTimeout(pauseTimer);
      pauseTimer = setTimeout(() => { paused = false; }, 3000);
    };

    // Only pause on direct horizontal interactions with the carousel
    el.addEventListener('mousedown', pauseScroll);
    el.addEventListener('touchstart', pauseScroll, { passive: true });
    el.addEventListener('wheel', (e) => {
      // Only pause if scrolling horizontally over the carousel
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) pauseScroll();
    }, { passive: true });

    rafId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(pauseTimer);
      el.removeEventListener('mousedown', pauseScroll);
      el.removeEventListener('touchstart', pauseScroll);
    };
  }, [clients]);

  return (
    <BaseSection ref={ref} style={{ backgroundImage: 'linear-gradient(to top, #000 0%, transparent 120px)' }}>
      <Grid item style={{width: '100%', textAlign: 'center'}}>
        <FadeInOnScroll>
          <SectionTitle onEnterViewport={() => autoUrlChange && navigate('/portfolio')}>
            {dictionary.portfolioSectionTitle}
          </SectionTitle>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1, mb: 3 }}>
            {dictionary.portfolioSectionSubtitle}
          </Typography>
        </FadeInOnScroll>

        {/* Metrics counters */}
        <Box
          ref={metricsRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 3, sm: 6 },
            mb: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <CurrencyExchangeIcon color="primary" sx={{ width: 28, height: 28 }}/>
              <Typography variant="h3" color="primary" fontWeight={700} fontFamily="'Montserrat', sans-serif">
                {revenue}M
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">Revenue Enabled</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <PeopleIcon color="secondary" sx={{ width: 28, height: 28 }}/>
              <Typography variant="h3" color="secondary" fontWeight={700} fontFamily="'Montserrat', sans-serif">
                {(users / 10).toFixed(1)}M+
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">Real Humans Helped</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <BusinessIcon sx={{ width: 28, height: 28, color: '#15fffb' }}/>
              <Typography variant="h3" fontWeight={700} fontFamily="'Montserrat', sans-serif" sx={{ color: '#15fffb' }}>
                {businesses}+
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">Businesses Propelled</Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap',
          px: 2,
          mb: 2,
          maxWidth: 700,
          mx: 'auto',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <Autocomplete
            multiple
            size="small"
            disableClearable
            options={industries}
            getOptionLabel={(option) => `${option} (${industryCounts[option]})`}
            value={selectedIndustries}
            onChange={(_, value) => setSelectedIndustries(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Industry"
                InputProps={{
                  ...params.InputProps,
                  readOnly: isMobile,
                  startAdornment: (
                    <>
                      <FilterListIcon sx={{ width: 16, height: 16, mr: 0.5, opacity: 0.5 }}/>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={`${option} (${industryCounts[option]})`} {...getTagProps({ index })} color="primary" variant="outlined"/>
              ))
            }
            sx={{ minWidth: isMobile ? '100%' : 280, maxWidth: '100%' }}
          />
          <Autocomplete
            multiple
            size="small"
            disableClearable
            options={countries}
            getOptionLabel={(option) => `${countryLabels[option] || option} (${countryCounts[option]})`}
            value={selectedCountries}
            onChange={(_, value) => setSelectedCountries(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Country"
                InputProps={{
                  ...params.InputProps,
                  readOnly: isMobile,
                  startAdornment: (
                    <>
                      <FilterListIcon sx={{ width: 16, height: 16, mr: 0.5, opacity: 0.5 }}/>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={`${countryLabels[option] || option} (${countryCounts[option]})`} {...getTagProps({ index })} color="secondary" variant="outlined"/>
              ))
            }
            sx={{ minWidth: isMobile ? '100%' : 280, maxWidth: '100%' }}
          />
        </Box>
      </Grid>
      <Grid item>
        <CarouselContainer ref={carouselRef}>
          <Grid container alignItems="flex-start" wrap="nowrap" spacing={isMobile ? 3 : 6}>
            {clients.map((client, index) => (
              <PortfolioSectionItem {...client} key={client.slug}/>
            ))}
          </Grid>
        </CarouselContainer>
      </Grid>
      <SectionCTA
        nextSectionLabel="Tech Knowledge & Experience"
        onNextSection={() => techStackSectionRef?.current?.scrollIntoView({ behavior: 'smooth' })}
      />
    </BaseSection>
  );
});
